import { DialogComponent } from '@theme/dialog';

/**
 * Backend endpoint for the virtual try-on feature.
 *
 * Currently pointed at the local dev server (`npm run dev:local` in
 * sunday-chat-api/ — see its README) since there's no deployment yet.
 * Swap this back to the real backend URL once one is deployed.
 */
const TRY_ON_API_URL = 'http://localhost:3000/api/try-on';

// Hard constraint: Vercel's request body ceiling (~4.5MB) is a fixed
// platform limit, not configurable. A 1024px-max-dimension JPEG at quality
// 0.8 typically lands around 100-400KB for a portrait photo — comfortably
// under this cap once base64-inflated (~33%), leaving generous headroom.
const MAX_DIMENSION = 1024;
const JPEG_QUALITY = 0.8;

const REQUEST_TIMEOUT_MS = 45_000;

/**
 * The `<try-on-dialog>` virtual try-on widget: upload a photo, compress it
 * client-side, send it + the product's image to sunday-chat-api, and show
 * the generated "wearing it" result in a photoshoot-style frame.
 *
 * Extends the shared `DialogComponent` (assets/dialog.js) for open/close/
 * outside-click/Escape mechanics — this widget only adds its own refs and
 * upload/generate/reset behavior on top.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element (required by DialogComponent).
 * @property {HTMLInputElement} fileInput - The hidden file input.
 * @property {HTMLElement} uploadPreviewPlaceholder - Placeholder text shown before a photo is chosen.
 * @property {HTMLImageElement} uploadPreviewImage - Preview of the chosen photo.
 * @property {HTMLElement} uploadError - Validation error message for the upload step.
 * @property {HTMLButtonElement} generateButton - Submits the try-on request.
 * @property {HTMLElement} uploadStep - The upload step container.
 * @property {HTMLElement} loadingStep - The loading step container.
 * @property {HTMLElement} resultStep - The result step container.
 * @property {HTMLElement} errorStep - The error step container.
 * @property {HTMLElement} loadingMessage - Rotating loading-status text.
 * @property {HTMLImageElement} resultImage - The generated image.
 * @property {HTMLElement} errorMessage - Error step's message text.
 *
 * @extends {DialogComponent<Refs>}
 */
export class TryOnDialog extends DialogComponent {
  requiredRefs = [
    'dialog',
    'fileInput',
    'uploadPreviewPlaceholder',
    'uploadPreviewImage',
    'uploadError',
    'generateButton',
    'uploadStep',
    'loadingStep',
    'resultStep',
    'errorStep',
    'loadingMessage',
    'resultImage',
    'errorMessage',
  ];

  /** @type {string | null} Compressed photo, ready to send — kept so a retry doesn't require re-uploading. */
  #compressedPhotoBase64 = null;

  /** @type {number | null} */
  #loadingMessageInterval = null;

  /**
   * Handles the file input's change event. Bound via
   * `on:change="/handleFileSelected"` in try-on-modal.liquid.
   *
   * @param {Event} event
   */
  async handleFileSelected(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const file = input.files?.[0];
    this.#hideUploadError();

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.#showUploadError();
      return;
    }

    try {
      this.#compressedPhotoBase64 = await compressImageFile(file, MAX_DIMENSION, JPEG_QUALITY);
    } catch (error) {
      console.error('[try-on] photo compression failed:', error);
      this.#showUploadError();
      return;
    }

    const { uploadPreviewPlaceholder, uploadPreviewImage, generateButton } = this.refs;
    uploadPreviewImage.src = this.#compressedPhotoBase64;
    uploadPreviewImage.classList.remove('hidden');
    uploadPreviewPlaceholder.classList.add('hidden');
    generateButton.disabled = false;
  }

  /**
   * Submits the compressed photo + product image to the backend. Bound via
   * `on:click="/generate"`. Also the retry action from the error step (the
   * already-compressed photo is kept, no re-upload needed).
   */
  async generate() {
    if (!this.#compressedPhotoBase64) return;

    this.#showStep('loading');
    this.#startLoadingMessageRotation();

    // Shopify's `image_url` Liquid filter returns a protocol-relative URL
    // (`//cdn.shopify.com/...`), which `new URL()` can't parse on its own —
    // resolve it against the current page so the backend always receives a
    // proper absolute https:// URL.
    const rawProductImageUrl = this.getAttribute('data-product-image-url') || '';
    const productImageUrl = rawProductImageUrl ? new URL(rawProductImageUrl, window.location.href).href : '';

    try {
      const response = await fetchWithTimeout(
        TRY_ON_API_URL,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            photoBase64: this.#compressedPhotoBase64,
            productImageUrl,
          }),
        },
        REQUEST_TIMEOUT_MS
      );

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || `Request failed (${response.status})`);
      }

      const data = await response.json();
      if (!data.imageBase64) throw new Error('No image returned');

      this.refs.resultImage.src = data.imageBase64;
      this.#showStep('result');
    } catch (error) {
      console.error('[try-on] generate failed:', error);
      this.refs.errorMessage.textContent = this.dataset.i18nError || '';
      this.#showStep('error');
    } finally {
      this.#stopLoadingMessageRotation();
    }
  }

  /**
   * Resets back to the upload step. Bound via `on:click="/reset"` from both
   * the result step's "try again" button and the error step's retry button.
   * Keeps the already-compressed photo so the user doesn't need to
   * re-upload for a retry.
   */
  reset() {
    this.#showStep('upload');
  }

  /**
   * @param {'upload' | 'loading' | 'result' | 'error'} step
   */
  #showStep(step) {
    const { uploadStep, loadingStep, resultStep, errorStep } = this.refs;
    uploadStep.classList.toggle('hidden', step !== 'upload');
    loadingStep.classList.toggle('hidden', step !== 'loading');
    resultStep.classList.toggle('hidden', step !== 'result');
    errorStep.classList.toggle('hidden', step !== 'error');
  }

  /**
   * @returns {string[]} The rotating loading-status messages, passed down
   * from try-on-modal.liquid as a JSON array in `data-i18n-loading-messages`
   * (this component has no direct access to Liquid's `| t` filter at
   * runtime, so translated strings are handed down as data attributes).
   */
  #getLoadingMessages() {
    try {
      const parsed = JSON.parse(this.dataset.i18nLoadingMessages || '[]');
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : [''];
    } catch {
      return [''];
    }
  }

  #startLoadingMessageRotation() {
    const messages = this.#getLoadingMessages();
    let index = 0;
    const { loadingMessage } = this.refs;
    const update = () => {
      loadingMessage.textContent = messages[index % messages.length];
      index += 1;
    };
    update();
    this.#loadingMessageInterval = window.setInterval(update, 4500);
  }

  #stopLoadingMessageRotation() {
    if (this.#loadingMessageInterval !== null) {
      window.clearInterval(this.#loadingMessageInterval);
      this.#loadingMessageInterval = null;
    }
  }

  #showUploadError() {
    const { uploadError } = this.refs;
    uploadError.textContent = this.dataset.i18nInvalidFile || '';
    uploadError.classList.remove('hidden');
  }

  #hideUploadError() {
    this.refs.uploadError.classList.add('hidden');
  }
}

if (!customElements.get('try-on-dialog')) {
  customElements.define('try-on-dialog', TryOnDialog);
}

/**
 * Compresses an uploaded image file to a JPEG data URL, capped at
 * `maxDimension` on its longest side. This is the client-side half of the
 * hard Vercel-body-size constraint documented above the constants.
 *
 * @param {File} file
 * @param {number} maxDimension
 * @param {number} quality
 * @returns {Promise<string>} A `data:image/jpeg;base64,...` URL.
 */
async function compressImageFile(file, maxDimension, quality) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error('Canvas toBlob failed'))),
      'image/jpeg',
      quality
    );
  });

  return await blobToDataUrl(/** @type {Blob} */ (blob));
}

/**
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(/** @type {string} */ (reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * fetch() with a client-side timeout — a hung request shouldn't leave the
 * user staring at a spinner forever. Routes to the same error handling as
 * any other failure.
 *
 * @param {string} url
 * @param {RequestInit} options
 * @param {number} timeoutMs
 * @returns {Promise<Response>}
 */
function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timeoutId));
}
